import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { getInitData } from '@/lib/tg';
import { TopBar } from '@/components/ui/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader2, Upload, X } from 'lucide-react';
import { BalanceWidget } from '@/components/BalanceWidget';

interface PhotoJob {
  id: string;
  mode: string;
  prompt: string | null;
  status: string;
  resultUrls: string[] | null;
  createdAt: string;
}

const MAX_PHOTOS = 4;

async function uploadPhotos(files: File[]): Promise<string[]> {
  const fd = new FormData();
  for (const f of files) fd.append('photos', f);
  const res = await fetch('/api/studios/upload', {
    method: 'POST',
    headers: { 'X-Telegram-Init-Data': getInitData() },
    body: fd,
  });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error?.error ?? 'upload_failed');
  return json.data.urls as string[];
}

export default function PhotoStudioPage() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'portrait' | 'edit'>('portrait');
  const [prompt, setPrompt] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const photoUrls = useMemo(() => photos.map((f) => URL.createObjectURL(f)), [photos]);
  useEffect(() => () => photoUrls.forEach(URL.revokeObjectURL), [photoUrls]);
  const [uploading, setUploading] = useState(false);

  const { data: jobs } = useQuery<PhotoJob[]>({
    queryKey: ['photo-jobs'],
    queryFn: () => api.get<PhotoJob[]>('/api/studios/photo'),
    refetchInterval: 15_000,
  });

  const submit = useMutation({
    mutationFn: async () => {
      setUploading(true);
      try {
        const urls = photos.length > 0 ? await uploadPhotos(photos) : [];
        return await api.post<{ id: string; status: string }>('/api/studios/photo', {
          mode,
          prompt,
          referencePhotos: urls,
        });
      } finally {
        setUploading(false);
      }
    },
    onSuccess: () => {
      setPrompt('');
      setPhotos([]);
      qc.invalidateQueries({ queryKey: ['photo-jobs'] });
    },
  });

  function onPickPhotos(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []).slice(0, MAX_PHOTOS - photos.length);
    setPhotos((prev) => [...prev, ...files].slice(0, MAX_PHOTOS));
    e.target.value = '';
  }

  function removePhoto(i: number) {
    setPhotos((prev) => prev.filter((_, idx) => idx !== i));
  }

  const canSubmit = (photos.length > 0 || prompt.trim().length >= 5);
  const needsPhotos = mode === 'edit' || mode === 'portrait';
  const estimatedUsd = 0.08; // nano-banana portrait/edit

  return (
    <div className="safe-bottom min-h-full">
      <TopBar title="Фото-студия" />
      <div className="p-4 space-y-3">
        <BalanceWidget estimatedUsd={estimatedUsd} />

        <Card className="space-y-3">
          <div className="font-semibold">Режим</div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant={mode === 'portrait' ? 'primary' : 'secondary'}
              onClick={() => setMode('portrait')}
            >
              Портрет
            </Button>
            <Button
              variant={mode === 'edit' ? 'primary' : 'secondary'}
              onClick={() => setMode('edit')}
            >
              Редактирование
            </Button>
          </div>
        </Card>

        <Card className="space-y-3">
          <div className="flex justify-between items-center">
            <div className="font-semibold">
              {mode === 'portrait' ? 'Фото лица' : 'Фото для редактирования'} ({photos.length}/{MAX_PHOTOS})
            </div>
            <span className="text-xs text-tg-hint">image2image</span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {photos.map((f, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden">
                <img src={photoUrls[i]} alt={`ref-${i}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 bg-black/60 rounded-full p-1"
                  aria-label="Убрать"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <label className="aspect-square rounded-xl border-2 border-dashed border-tg-hint/40 flex flex-col items-center justify-center text-tg-hint cursor-pointer hover:bg-tg-secondaryBg/40">
                <Upload className="w-5 h-5" />
                <span className="text-[10px] mt-1">Добавить</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={onPickPhotos} />
              </label>
            )}
          </div>
          {needsPhotos && photos.length === 0 && (
            <p className="text-xs text-red-500">
              Этот режим требует хотя бы 1 фото-референс.
            </p>
          )}
        </Card>

        <Card>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            className="w-full min-h-[100px] p-3 bg-tg-bg rounded-xl outline-none resize-none"
            placeholder={
              mode === 'portrait'
                ? 'Опиши стиль портрета: обстановка, свет, одежда…'
                : 'Опиши что изменить в присланной фотографии…'
            }
          />
        </Card>

        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={!canSubmit || submit.isPending || uploading || (needsPhotos && photos.length === 0)}
          onClick={() => submit.mutate()}
        >
          {(submit.isPending || uploading) && (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          )}
          {uploading ? 'Загрузка фото…' : 'Сгенерировать'}
        </Button>

        {jobs && jobs.length > 0 && (
          <Card className="space-y-2">
            <div className="font-semibold">История</div>
            {jobs.slice(0, 10).map((j) => (
              <div
                key={j.id}
                className="flex items-center justify-between gap-2 text-sm py-1 border-b border-tg-secondaryBg/60 last:border-0"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="font-mono text-xs text-tg-hint">#{j.id}</span>
                  <span>{j.mode}</span>
                  <span
                    className={
                      j.status === 'done'
                        ? 'text-green-500 text-xs'
                        : j.status === 'failed'
                          ? 'text-red-500 text-xs'
                          : 'text-tg-hint text-xs'
                    }
                  >
                    {j.status === 'running' ? '📸 генерация…' : j.status}
                  </span>
                </div>
                {j.resultUrls && j.resultUrls.length > 0 && (
                  <a
                    href={j.resultUrls[0]}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-brand-orange"
                  >
                    Открыть
                  </a>
                )}
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}
